import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function EcoTourismVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "eco-tourism-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  // You can rename or add more sections as needed
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park",
    "section-2": "Pacific Spirit Park",
    "section-3": "Capilano Bridge",
    "section-4": "Lynn Canyon",
    "section-5": "Grouse Mountain",
    "section-6": "Lighthouse Park",
    "section-7": "VanDusen Garden",
    accommodations: "Eco-Friendly Accommodations",
    tours: "Responsible Tour Operators",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Insert relevant sources or references
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "Stanley Park Official Site",
    },
    {
      url: "https://metrovancouver.org/services/regional-parks/park/pacific-spirit-regional-park",
      label: "Pacific Spirit Regional Park",
    },
    {
      url: "https://lynncanyon.ca/",
      label: "Lynn Canyon Park & Ecology Centre",
    },
    {
      url: "https://capbridge.com",
      label: "Capilano Suspension Bridge Park",
    },
    {
      url: "https://grousemountain.com",
      label: "Grouse Mountain Resort",
    },
    {
      url: "https://westvancouver.ca/parks-recreation/parks-trails/lighthouse-park",
      label: "Lighthouse Park",
    },
    {
      url: "http://vandusengarden.org/",
      label: "VanDusen Botanical Garden",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each `id` matches the <h2> or <p> "id" in the article text
  const locations = [
    {
      id: "section-1",
      lat: 49.30,
      lng: -123.14,
      popupText: "Stanley Park (49.30 N, -123.14 W)",
    },
    {
      id: "section-2",
      lat: 49.25,
      lng: -123.21,
      popupText: "Pacific Spirit Park (49.25 N, -123.21 W)",
    },
    {
      id: "section-3",
      lat: 49.34278,
      lng: -123.11222,
      popupText: "Capilano Suspension Bridge (49.34278 N, -123.11222 W)",
    },
    {
      id: "section-4",
      lat: 49.3338,
      lng: -123.0175,
      popupText: "Lynn Canyon Park (49.3338 N, -123.0175 W)",
    },
    {
      id: "section-5",
      lat: 49.3772,
      lng: -123.0816,
      popupText: "Grouse Mountain (49.3772 N, -123.0816 W)",
    },
    {
      id: "section-6",
      lat: 49.336,
      lng: -123.262,
      popupText: "Lighthouse Park (49.336 N, -123.262 W)",
    },
    {
      id: "section-7",
      lat: 49.2396,
      lng: -123.1329,
      popupText: "VanDusen Garden (49.2396 N, -123.1329 W)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Eco-Tourism: Vancouver’s Best Spots</title>
        <meta
          name="description"
          content="Explore Vancouver's top nature spots, eco-friendly accommodations, and sustainable tour operators."
        />
        <meta
          name="keywords"
          content="Vancouver, eco-tourism, nature, sustainability, travel, Canada"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/eco-tourism-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Eco-Tourism: Vancouver’s Best Spots"
        />
        <meta
          property="og:description"
          content="Discover Stanley Park, Pacific Spirit, and more. Plan a sustainable Vancouver trip!"
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/eco-tourism-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/eco-tourism-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Eco-Tourism: Vancouver’s Best Spots"
        />
        <meta
          name="twitter:description"
          content="Discover Stanley Park, Pacific Spirit, and more. Plan a sustainable Vancouver trip!"
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/eco-tourism-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "/assets/images/eco-tourism-vancouver.jpg"}
              alt={ref_data?.imageDesc || "Explore Vancouver's Eco-Friendly Destinations"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a coastal city in British Columbia that blends 
              urban convenience with breathtaking nature. From towering 
              rainforests to tranquil beaches, Vancouver has abundant green 
              spaces and a passionate commitment to sustainability. In this 
              guide, we’ll explore top eco-friendly locations, how to reach 
              them car-free, and where to stay responsibly.
            </p>

            {/* SECTION: Stanley Park */}
            <h2 id="section-1">Stanley Park</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Stanley Park (49.30 N, -123.14 W)</strong>{" "}
              <Marker
                coordY={49.30}
                coordX={-123.14}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – This 405-hectare urban park features ancient cedar and fir 
              forests, scenic seawall paths, and abundant wildlife. Located 
              next to downtown Vancouver, Stanley Park is easily accessible 
              on foot, by bike, or via public bus. Don’t miss the Vancouver 
              Aquarium for marine conservation exhibits, or the Stanley Park 
              Ecology Society’s guided nature walks.
            </p>

            {/* SECTION: Pacific Spirit Regional Park */}
            <h2 id="section-2">Pacific Spirit Park</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Pacific Spirit Park (49.25 N, -123.21 W)</strong>{" "}
              <Marker
                coordY={49.25}
                coordX={-123.21}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Surrounding the University of British Columbia, Pacific 
              Spirit Park offers 73 km of forested trails, beaches along 
              the Point Grey peninsula, and unique habitats like Camosun Bog. 
              A favorite spot for trail running, bird watching, and dog walking, 
              it’s accessible via bus routes to UBC or by cycling along 
              dedicated bike lanes.
            </p>

            {/* SECTION: Capilano Bridge */}
            <h2 id="section-3">Capilano Suspension Bridge</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Capilano Suspension Bridge (49.34278 N, -123.11222 W)</strong>{" "}
              <Marker
                coordY={49.34278}
                coordX={-123.11222}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Famous for its swaying footbridge high above the 
              Capilano River, this private nature park also features 
              rainforest boardwalks and Indigenous cultural exhibits. 
              A free shuttle from downtown (in peak season) allows 
              car-free visits. For a quieter, free alternative, try 
              Lynn Canyon Park just to the east.
            </p>

            {/* SECTION: Lynn Canyon */}
            <h2 id="section-4">Lynn Canyon</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Lynn Canyon Park (49.3338 N, -123.0175 W)</strong>{" "}
              <Marker
                coordY={49.3338}
                coordX={-123.0175}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – This free municipal park in North Vancouver boasts its 
              own 50-metre-high suspension bridge over a forested canyon, 
              plus waterfalls, swimming holes, and an ecology centre. Reach 
              it by taking the SeaBus from Waterfront Station to Lonsdale 
              Quay, then a local bus.
            </p>

            {/* SECTION: Grouse Mountain */}
            <h2 id="section-5">Grouse Mountain</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Grouse Mountain (49.3772 N, -123.0816 W)</strong>{" "}
              <Marker
                coordY={49.3772}
                coordX={-123.0816}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Known as the “Peak of Vancouver,” Grouse Mountain 
              offers spectacular views, the challenging Grouse Grind hiking 
              trail, winter sports, and a wildlife refuge for rescued grizzly 
              bears. Hop on the SeaBus to Lonsdale Quay and then bus #236 
              to reach the base.
            </p>

            {/* SECTION: Lighthouse Park */}
            <h2 id="section-6">Lighthouse Park</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Lighthouse Park (49.336 N, -123.262 W)</strong>{" "}
              <Marker
                coordY={49.336}
                coordX={-123.262}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A coastal old-growth forest in West Vancouver, 
              Lighthouse Park features some of the region’s oldest cedar and 
              Douglas fir trees, rocky beaches, and the historic Point 
              Atkinson Lighthouse. Bus #250 from downtown takes you within 
              walking distance of the park entrance.
            </p>

            {/* SECTION: VanDusen Botanical Garden */}
            <h2 id="section-7">VanDusen Garden</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>VanDusen Botanical Garden (49.2396 N, -123.1329 W)</strong>{" "}
              <Marker
                coordY={49.2396}
                coordX={-123.1329}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A tranquil 55-acre botanical oasis in the heart of 
              Vancouver, VanDusen offers globally-themed plant collections, 
              a hedge maze, and a LEED Platinum visitor center. Transit-friendly, 
              located near Oak Street and 37th Ave.
            </p>

            {/* SECTION: Eco-Friendly Accommodations */}
            <h2 id="accommodations">Eco-Friendly Accommodations</h2>
            <p>
              Vancouver has numerous sustainable places to stay. 
              The YWCA Hotel invests in community programs and composting; 
              The Listel Hotel is 5 Green Key certified and focuses on 
              zero waste; Fairmont Waterfront hosts a rooftop bee apiary; 
              and The Burrard provides free bike rentals and EV charging. 
              Seek hotels labeled “Green Key” or “LEED” for assured eco-credentials.
            </p>

            {/* SECTION: Responsible Tour Operators */}
            <h2 id="tours">Responsible Tour Operators</h2>
            <p>
              Local companies follow stringent guidelines to minimize 
              wildlife disturbance and environmental impact. Prince of Whales 
              and Wild Whales Vancouver run whale-watching tours under 
              eco-friendly protocols. Cycle City Tours offers guided city 
              bike explorations. Indigenous-led Talaysay Tours connects you 
              with First Nations culture through guided walks and paddles. 
              Deep Cove Kayak and Takaya Tours facilitate eco-friendly 
              paddling adventures.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to visit for eco-tourism?</h3>
              <p>
                Summer (June–August) offers long days and ideal weather for 
                hiking and paddling, but spring and fall bring fewer crowds 
                and vibrant seasonal changes. Winter is perfect for 
                snowshoeing or skiing on nearby mountains.
              </p>

              <h3>2. Are there volunteer or conservation opportunities for visitors?</h3>
              <p>
                Absolutely. Organizations like the Stanley Park Ecology 
                Society and the Great Canadian Shoreline Cleanup often 
                welcome short-term volunteers. Check local park websites 
                for schedules of tree planting or park restoration days.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s dedication to green city planning, accessible 
              public transit, and protected natural areas makes it a top 
              eco-tourism destination. Whether you’re exploring Stanley Park’s 
              trails, kayaking through coastal waters, or observing wildlife 
              from a respectful distance, you’ll find countless ways to 
              travel sustainably while enjoying Canada’s West Coast at its best.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}